import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { Eye, EyeOff } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import api from "../../api/api.js";
import VerifyCodeForm from "./VerifyCodeForm";

const isPasswordValid = (pw) => pw.length >= 8 && /[A-Z]/.test(pw);

// Three-step, single-page reset: enter email → enter the 6-digit code (same OTP
// as signup email verification) → set a new password. No magic link / URL token.
export default function ForgotPassword() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [step, setStep] = useState("email"); // "email" | "otp" | "password"
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Step 3 (new password) state.
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pwError, setPwError] = useState("");
    const [pwLoading, setPwLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const passwordMeetsRequirements = isPasswordValid(password);
    const passwordsMatch = confirm.length > 0 && password === confirm;

    // Step 1: send the code. Anti-enumeration — always advance to the code step,
    // whether or not the email matched an account.
    const handleSendCode = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setStep("otp");
        } catch (err) {
            setError(err.response?.data?.error || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 3: set the new password. Authorized by the reset session the backend set
    // when the OTP verified (no token in the request).
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setPwError("");
        if (!passwordMeetsRequirements) {
            setPwError("Password must be at least 8 characters and contain one uppercase letter.");
            return;
        }
        if (password !== confirm) {
            setPwError("Passwords do not match.");
            return;
        }
        setPwLoading(true);
        try {
            await api.post("/auth/reset-password", { new_password: password });
            setSuccess(true);
            setTimeout(() => navigate("/login"), 2000);
        } catch (err) {
            setPwError(err.response?.data?.error || "Failed to reset password.");
        } finally {
            setPwLoading(false);
        }
    };

    const title = step === "password" ? "Set a New Password" : "Forgot Password";

    const inputClass = `w-full rounded-xl border px-4 py-3 pr-11 text-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-400/50 ${
        isDarkMode
            ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400 focus:border-primary-500'
            : 'border-slate-300 bg-slate-50 text-slate-900 placeholder-slate-500 focus:border-primary-500'
    }`;
    const eyeButtonClass = `absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
        isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
    }`;

    return (
        <div className={`min-h-screen transition-colors duration-500 ${
            isDarkMode
                ? 'bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-color-slate-800)_10%,_var(--tw-color-slate-950))]'
                : 'bg-white bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-color-primary-50)_0%,_var(--tw-color-white))]'
        }`}>
            <div className="flex items-center justify-center min-h-screen px-4 pb-12">
                <div className={`w-full max-w-md rounded-3xl p-10 shadow-xl backdrop-blur-sm transition-all duration-300 ${
                    isDarkMode
                        ? 'bg-slate-900/80 shadow-slate-900/50'
                        : 'border border-slate-100 bg-white/90 shadow-primary-200/50'
                }`}>
                    {/* Back to Sign In — only on the first (email) step. Once the reset
                        is under way (code / new password / done) there's no back button. */}
                    {step === "email" && (
                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            aria-label="Back to sign in"
                            title="Back to sign in"
                            className={`inline-flex items-center justify-center w-10 h-10 mb-6 rounded-full border transition-colors ${
                                isDarkMode
                                    ? 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200 hover:border-slate-600'
                                    : 'border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-300'
                            }`}
                        >
                            <FiArrowLeft size={18} />
                        </button>
                    )}
                    <h2 className={`text-3xl font-extrabold text-center mb-4 ${
                        isDarkMode ? 'text-slate-50' : 'text-slate-900'
                    }`}>
                        {title}
                    </h2>

                    {step === "email" && (
                        <>
                            <p className={`text-sm text-center mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                Enter the email associated with your account and we'll send you a 6-digit code to reset your password.
                            </p>
                            <form className="space-y-4" onSubmit={handleSendCode}>
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className={`w-full rounded-xl border px-4 py-3 text-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-400/50 ${
                                        isDarkMode
                                            ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400 focus:border-primary-500'
                                            : 'border-slate-300 bg-slate-50 text-slate-900 placeholder-slate-500 focus:border-primary-500'
                                    }`}
                                />

                                {error && (
                                    <p className="text-xs text-red-500 font-medium">{error}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full rounded-xl bg-primary-600 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-primary-500/30 hover:bg-primary-700 transition-all duration-300 active:scale-[0.98] disabled:opacity-60"
                                >
                                    {loading ? "Sending..." : "Send Code"}
                                </button>
                            </form>

                            <p className={`text-center text-sm mt-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                Remembered your password?{" "}
                                <Link
                                    to="/login"
                                    className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                                >
                                    Sign in
                                </Link>
                            </p>
                        </>
                    )}

                    {step === "otp" && (
                        <VerifyCodeForm
                            email={email}
                            verifyEndpoint="/auth/forgot-password/verify"
                            resendEndpoint="/auth/forgot-password"
                            onVerified={() => setStep("password")}
                            description="Enter the 6-digit code we emailed you, then set a new password."
                        />
                    )}

                    {step === "password" && (
                        success ? (
                            <p className="text-primary-500 font-semibold text-center">
                                Password reset successful! Redirecting to sign in...
                            </p>
                        ) : (
                            <form className="space-y-4" onSubmit={handleResetPassword}>
                                <div>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="New password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className={inputClass}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className={eyeButtonClass}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {password && (
                                        <p className={`mt-1 ml-1 text-xs font-medium ${
                                            passwordMeetsRequirements ? 'text-primary-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')
                                        }`}>
                                            {passwordMeetsRequirements ? "✓ Requirements met" : "Requires 8+ characters and one uppercase letter"}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div className="relative">
                                        <input
                                            type={showConfirm ? "text" : "password"}
                                            placeholder="Confirm new password"
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            required
                                            className={inputClass}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirm((prev) => !prev)}
                                            className={eyeButtonClass}
                                            aria-label={showConfirm ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {confirm && (
                                        <p className={`mt-1 ml-1 text-xs font-medium ${
                                            passwordsMatch ? 'text-primary-500' : 'text-red-500'
                                        }`}>
                                            {passwordsMatch ? "✓ Passwords match" : "Passwords do not match"}
                                        </p>
                                    )}
                                </div>

                                {pwError && (
                                    <p className="text-xs text-red-500 font-medium">{pwError}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={pwLoading}
                                    className="w-full rounded-xl bg-primary-600 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-primary-500/30 hover:bg-primary-700 transition-all duration-300 active:scale-[0.98] disabled:opacity-60"
                                >
                                    {pwLoading ? "Resetting..." : "Reset Password"}
                                </button>
                            </form>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
